import { Link } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import Seo from "../components/site/Seo";
import { PageHero, Section, CtaBand, Disclaimer } from "../components/site/Bits";

const EXPECT = [
  { title: "A friendly first call", body: "We'll ask what's happening at home, what you'd like to do more easily, and who supports you." },
  { title: "We talk to your therapist", body: "If you have an OT or physio, we'll work with them to confirm what to trial. If you don't, we can explain your options." },
  { title: "Equipment comes to you", body: "We deliver and set up the trial equipment, and show you and your carers how to use it safely." },
  { title: "You try it in daily life", body: "Use it during your normal routine. Tell us honestly what works and what doesn't." },
  { title: "Paperwork handled", body: "We write up the trial and prepare a quote to support your NDIS funding request." },
  { title: "Keep it, or we collect it", body: "If funding is approved the equipment is yours. If it's not suitable or not approved, we pick it up." },
];
const NEED = ["Your NDIS number and plan dates, if you have them", "How your plan is managed (self-, plan- or agency-managed)", "Your treating therapist's details, if you have one", "Your support coordinator's details, if you have one", "A rough idea of the task you need help with", "For home modifications: whether you own or rent, and landlord details if renting"];

export default function Participants() {
  return (
    <>
      <Seo title="For NDIS Participants & Families" path="/participants-families"
        description="What to expect from an NDIS equipment trial or home modification in Melbourne, how costs and funding work, and what you'll need to get started." />
      <PageHero eyebrow="For participants and families" title="What to expect, in plain language"
        intro="You stay in control the whole way. Nothing is purchased until you've tried it and you're happy, and funding has been approved."
        image="/images/dining.jpg" imageAlt="A bright, accessible dining and living space at home">
        <Link to="/request-trial" className="btn-primary">Request a Trial</Link>
        <Link to="/contact" className="btn-outline">Ask us a question</Link>
      </PageHero>
      <Section eyebrow="Step by step" title="What happens when you contact us">
        <ol className="grid gap-5 md:grid-cols-2">
          {EXPECT.map((e, i) => (
            <li key={e.title} className="card flex gap-5 p-6">
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-amber font-display font-bold text-on-amber" aria-hidden="true">{i + 1}</span>
              <div><h3 className="font-display text-xl font-semibold"><span className="sr-only">Step {i + 1}: </span>{e.title}</h3><p className="mt-1 text-ink-muted">{e.body}</p></div>
            </li>
          ))}
        </ol>
      </Section>
      <Section tone="bg-surface" eyebrow="Costs and funding" title="How it's paid for">
        <div className="grid gap-8 lg:grid-cols-2">
          <div className="space-y-4 text-lg">
            <p>Most equipment and home modifications are funded through the <strong>Capital Supports</strong> part of your NDIS plan. Some low-cost items can come from your <strong>Core Supports</strong> budget.</p>
            <p>For higher-cost items, the NDIA usually needs evidence that the equipment suits you, such as a trial report and a quote. We prepare these with your therapist.</p>
            <p>We'll always explain any costs to you in writing before anything happens. You won't be charged for equipment you don't keep.</p>
            <Link to="/ndis-funding" className="btn-outline">Learn more about NDIS funding</Link>
          </div>
          <div className="card p-7">
            <h3 className="font-display text-2xl font-semibold">What you'll need</h3>
            <p className="mt-2 text-ink-muted">Don't worry if you don't have everything. We can help.</p>
            <ul className="mt-5 space-y-3">{NEED.map((n) => <li key={n} className="flex gap-3"><CheckCircle2 className="mt-1 shrink-0 text-success" size={20} aria-hidden="true" />{n}</li>)}</ul>
          </div>
        </div>
        <div className="mt-10"><Disclaimer /></div>
      </Section>
      <CtaBand />
    </>
  );
}
